import { useState, useEffect } from "react";
import { GoogleLogin } from "@react-oauth/google";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuthContext } from "../../context/context";
import { trackLogin } from "../../utils/analytics";
import { useToast } from "../../context/ToastContext";

const GoogleLoginButton = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuthContext();
  const { showToast } = useToast();
  const [isLoading, setIsLoading] = useState(false);

  // Check if we're already logged in
  useEffect(() => {
    if (auth.loggedIn) {
      // If we're on the login page and already logged in, redirect to home
      if (location.pathname === "/login") {
        navigate("/");
      }
    }
  }, [location.pathname, navigate]);

  const handleSuccess = async (credentialResponse) => {
    try {
      setIsLoading(true);
      const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:3333";

      // Send the token to your backend
      const response = await fetch(`${apiUrl}/auth/google`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include", // Important for cookies
        body: JSON.stringify({
          credential: credentialResponse.credential,
        }),
      });

      if (!response.ok) {
        const contentType = response.headers.get("content-type");
        let errorData;

        if (contentType && contentType.includes("application/json")) {
          errorData = await response.json();
        } else {
          const text = await response.text();
          console.error("Non-JSON error response:", text);
          errorData = { message: "Server error occurred" };
        }

        throw new Error(errorData.message || "Google login failed");
      }

      const data = await response.json();

      console.log('🔐 Google login response:', {
        hasToken: !!data.token,
        hasUserData: !!data.userData,
        goalsCount: data.userData?.goals?.length || 0,
        milestonesCount: data.userData?.milestones?.length || 0,
        notesCount: data.userData?.notes?.length || 0,
        milestones: data.userData?.milestones,
        userData: data.userData
      });

      if (!data.token || !data.userData) {
        throw new Error("Invalid response from server");
      }

      // Track successful Google login
      trackLogin("google");

      // CRITICAL: Store the auth token
      localStorage.setItem("authToken", data.token);

      // Store user data in localStorage
      localStorage.setItem("userData", JSON.stringify(data.userData));

      // Update auth context
      auth.setAuthStatus("success");
      auth.setLoggedIn(true);
      auth.setUserData(data.userData);

      // Fetch complete user data with all nested information (goals, finance, etc.)
      try {
        await auth.refetchUserData();
      } catch (refetchError) {
        console.error("Error refetching user data:", refetchError);
        // Continue anyway - we have basic userData
      }

      // Navigate after ensuring state is fully updated
      navigate("/");
    } catch (error) {
      console.error("Error during Google login:", error);
      showToast(error.message || "Failed to login with Google", "error");

      // Clean up any partial auth state
      localStorage.removeItem("userData");
      auth.setAuthStatus("failure");
      auth.setLoggedIn(false);
      auth.setUserData(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleError = () => {
    console.error("Google login failed");
    showToast("Failed to login with Google", "error");
  };

  return (
    <div className="w-auto flex flex-col items-center my-4">
      <GoogleLogin
        onSuccess={handleSuccess}
        onError={handleError}
        useOneTap={false} // Disable One Tap to prevent FedCM errors
        theme="outline"
        size="large"
        text="continue_with"
        shape="rectangular"
        width="250"
        disabled={isLoading}
      />
    </div>
  );
};

export default GoogleLoginButton;
